import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'

import { DateIcon } from '~assets/icons'
import { Icon, IconButton, TextField, Typography } from '~components/internal'
import { lightTokens } from '~tokens/internal'

export interface DueDateEditorProps {
  due: string | null | undefined
  onUpdate: (hours: number | null, minutes: number | null) => void
}

const DueDateEditor: React.FC<DueDateEditorProps> = ({ due, onUpdate }) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [hours, setHours] = useState<number | null>(null)
  const [minutes, setMinutes] = useState<number | null>(null)

  const isBlank = (): boolean => {
    return hours === null && minutes === null
  }

  const headerString = (): string => {
    if (isBlank() || expanded) {
      return 'Due Date'
    } else {
      const hourString = hours ? `${hours} hours` : ''
      const minuteString = minutes ? `${minutes} minutes` : ''
      const middle = hourString && minuteString ? ', ' : ''

      return `${hourString}${middle}${minuteString}`
    }
  }

  useEffect(() => {
    if (!due) {
      setHours(null)
      setMinutes(null)
      return
    }
    const match = due.match(/(?:(\d+)\s+)?(\d{2,}):(\d{2})/)
    if (match) {
      const days = match[1] ? parseInt(match[1], 10) : 0
      setHours(days * 24 + parseInt(match[2], 10))
      setMinutes(parseInt(match[3], 10))
    }
  }, [due])

  return (
    <Box>
      <Box
        className='flex-v-align'
        sx={{
          gap: 1,
        }}
      >
        <DateIcon color={isBlank() ? 'black' : lightTokens.primary.main} />
        <Typography variant='button' color={isBlank() ? 'black' : lightTokens.primary.main}>
          {headerString()}
        </Typography>
        <Box className='flex-grow' />
        <IconButton onClick={() => setExpanded(!expanded)}>
          <Icon name='ExpandMore' style={{ transform: expanded ? 'rotate(180deg)' : '' }} />
        </IconButton>
      </Box>
      {expanded && (
        <Box
          sx={{
            gap: 3,
          }}
        >
          <Typography variant='button'>Time to Complete</Typography>
          <Typography
            variant='body2'
            sx={{
              mt: 1,
            }}
          >
            Receive notifications if the step is not completed within the specified time
          </Typography>
          <Box
            className='flex-v-align'
            sx={{
              my: 1.5,
            }}
          >
            <Box
              sx={{
                width: '75%',
              }}
            >
              <TextField
                type='number'
                value={hours}
                onChange={(e) => {
                  const newVal =
                    e.target.value && e.target.value !== '' ? parseInt(e.target.value) : null
                  setHours(newVal)
                  onUpdate(newVal, minutes)
                }}
              />
            </Box>
            <Typography
              variant='body1'
              sx={{
                ml: 2,
              }}
            >
              Hours
            </Typography>
          </Box>
          <Box
            className='flex-v-align'
            sx={{
              my: 1.5,
            }}
          >
            <Box
              sx={{
                width: '75%',
              }}
            >
              <TextField
                type='number'
                value={minutes}
                onChange={(e) => {
                  const newVal =
                    e.target.value && e.target.value !== '' ? parseInt(e.target.value) : null
                  setMinutes(newVal)
                  onUpdate(hours, newVal)
                }}
              />
            </Box>
            <Typography
              variant='body1'
              sx={{
                ml: 2,
              }}
            >
              Minutes
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default DueDateEditor
